import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";

const departmentColors = [
  { name: "Mavi", value: "#3B82F6" },
  { name: "Mor", value: "#8B5CF6" },
  { name: "Pembe", value: "#EC4899" },
  { name: "Kırmızı", value: "#EF4444" },
  { name: "Turuncu", value: "#F97316" },
  { name: "Sarı", value: "#EAB308" },
  { name: "Yeşil", value: "#22C55E" },
  { name: "Turkuaz", value: "#14B8A6" },
  { name: "Camgöbeği", value: "#06B6D4" },
  { name: "Indigo", value: "#6366F1" },
  { name: "Koyu Gri", value: "#64748B" },
  { name: "Gri", value: "#6B7280" },
];

export default function EditDepartmentPage() {
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>();
  const hasEditPermission = checkPermission("department_update");

  useEffect(() => {
    if (!hasEditPermission) {
      navigate("/employees");
    }
  }, [hasEditPermission, navigate]);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const fieldClassName =
    "w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-300 hover:bg-white focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-50";
  const sectionClassName = "rounded-xl border border-slate-200 bg-white p-4 sm:p-5";
  const sectionBadgeClassName =
    "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700";
  const [form, setForm] = useState({
    name: "",
    description: "",
    parentId: null as string | null,
    color: "#3B82F6",
    isDefault: false,
  });

  useEffect(() => {
    const load = async () => {
      if (!departmentId) return;
      setError("");
      try {
        setInitialLoading(true);
        const dept = await ApiService.getDepartment(departmentId);
        setForm({
          name: dept.name || "",
          description: dept.description || "",
          parentId: dept.parentId || null,
          color: dept.color || "#3B82F6",
          isDefault: !!dept.isDefault,
        });
      } catch (e: any) {
        setError(e?.message || "Departman bilgileri yüklenemedi");
      } finally {
        setInitialLoading(false);
      }
    };
    load();
  }, [departmentId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    if (type === "checkbox") setForm((p) => ({ ...p, [name]: checked }));
    else setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!departmentId) return;
    if (!form.name) {
      setError("Lütfen departman adını girin.");
      return;
    }
    try {
      setLoading(true);
      await ApiService.updateDepartment(departmentId, {
        name: form.name,
        description: form.description,
        parentId: form.parentId || null,
        color: form.color,
        isDefault: !!form.isDefault,
      });
      navigate("/employees?tab=departments");
    } catch (e: any) {
      setError(e?.message || "Departman güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  if (!hasEditPermission) {
    return null;
  }

  return (
    <div className="h-[88vh] bg-gray-100 overflow-hidden flex flex-col">
      <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">Üyeler</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <button onClick={() => navigate("/employees?tab=users")} className={`pb-3 transition-all duration-200 text-gray-500 hover:text-gray-900`}>
                Kullanıcılar
              </button>
              <button
                onClick={() => navigate("/employees?tab=departments")}
                className={`pb-3 transition-all duration-200 text-gray-900 font-semibold border-b-2 border-gray-900`}>
                Departmanlar
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-5 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
            <div className="mb-5">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Departman Düzenle</h1>
              <p className="text-sm text-gray-600 mt-1">Departman bilgilerini güncelleyin ve hiyerarşisini düzenleyin.</p>
              <div className="mt-3 h-1 w-20 rounded-full bg-blue-200" />
            </div>

            {initialLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">{error}</div>}

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={sectionBadgeClassName}>
                      Temel Bilgiler
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Departman Adı *</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className={fieldClassName}
                      placeholder="Departman Adı"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Üst Departman</label>
                    <DepartmentSelector
                      value={form.parentId}
                      onChange={(parentId) => setForm((p) => ({ ...p, parentId: parentId || null }))}
                      placeholder="Üst Departman Seçin"
                      excludeIds={departmentId ? [departmentId] : []}
                      className="w-full rounded-xl"
                      allowClear={true}
                      showColors={true}
                    />
                  </div>
                </div>
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={sectionBadgeClassName}>
                      Aciklama
                    </span>
                  </div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={4}
                    className={fieldClassName}
                    placeholder="Departman açıklaması"
                  />
                </div>

                <div className={sectionClassName}>
                  <div className="mb-3">
                    <span className={sectionBadgeClassName}>
                      Gorunum ve Ayarlar
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Renk</label>
                    <div className="grid grid-cols-6 gap-2">
                      {departmentColors.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setForm((p) => ({ ...p, color: c.value }))}
                          className={`relative w-10 h-10 rounded-lg shadow-sm transition-transform hover:scale-105 ${
                            form.color === c.value ? "ring-2 ring-blue-400" : ""
                          }`}
                          style={{ backgroundColor: c.value }}
                          title={c.name}
                        />
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Seçili Renk: {form.color}</p>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">Varsayılan Departman</span>
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, isDefault: !p.isDefault }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        form.isDefault ? "bg-blue-600" : "bg-gray-300"
                      }`}
                      aria-pressed={!!form.isDefault}>
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          form.isDefault ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
                </div>

                <div className="sticky bottom-0 z-10 -mx-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="px-4 py-2 bg-white text-gray-700 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-sm">
                      {loading ? "Güncelleniyor..." : "Departmanı Güncelle"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
